import appSettings from "@/appsettings.json";
import ResponsibilityAgreementModal from "@/components/modals/ResponsibilityAgreementModal";
import { SessionContext } from "@/context/SessionContext";
import ConductionAPI from "@/javascript/conduction-api";
import {
	InstrumentalistTask,
	ProjectSubmission,
	UploadStatus,
} from "@/javascript/project-objects";
import { Utils } from "@/javascript/utils";
import {
	ActionIcon,
	Badge,
	Box,
	Button,
	Card,
	Group,
	Loader,
	ScrollArea,
	Skeleton,
	Stack,
	Table,
	Text,
	Tooltip,
	useMantineTheme
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
	IconAlertCircle,
	IconCheck,
	IconDownload,
	IconExclamationMark,
	IconFileDescription,
	IconFileMusic,
	IconFileUpload,
	IconLoader,
	IconQuestionMark,
	IconRefresh,
	IconUpload,
	IconUserCircle,
} from "@tabler/icons-react";
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { Countdown } from "../misc/Countdown";
import { AgreementStatement } from "../modals/ResponsibilityAgreementModal";

/* ----------------------------- helpers ---------------------------- */

const asDate = (d: Date | string | null | undefined): Date | null => {
	if (!d) return null;
	if (d instanceof Date) return isNaN(+d) ? null : d;

	// if the string has no timezone info, assume UTC
	const hasTZ = /[zZ]|[+-]\d{2}:\d{2}$/.test(d);
	const iso = hasTZ ? d : d + "Z";

	const parsed = new Date(iso);
	return isNaN(+parsed) ? null : parsed;
};

// show in the user’s local timezone
const toLocal = (
	d: Date | string | null | undefined,
	isMobile: boolean = false
): string => {
	const date = asDate(d);
	if (!date) return "—";

	const optsMobile: Intl.DateTimeFormatOptions = {
		month: "short",
		day: "numeric",
	};

	const optsDesktop: Intl.DateTimeFormatOptions = {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
	};

	return date.toLocaleString(undefined, isMobile ? optsMobile : optsDesktop);
};

type UploadStateKey =
	| "processing"
	| "completed"
	| "submitAgain"
	| "pastDue"
	| "notSubmitted"
	| "error";

type UploadState = {
	key: UploadStateKey;
	label: string;
	icon: (size: number) => React.ReactNode;
};

const computeUploadState = (
	status: string | null | undefined,
	fulfilled: Date | string | null | undefined,
	due: Date | string | null | undefined,
	isMobile: boolean
): UploadState => {
	const now = new Date();
	const dueD = asDate(due);
	if (status === "Uploading")
		return {
			key: "processing",
			label: "Processing",
			icon: (s) => <IconLoader size={s} />,
		};
	if (status === "Error")
		return {
			key: "error",
			label: isMobile ? "Upload" : "Not Yet Submitted",
			icon: (s) => <IconAlertCircle size={s} />,
		};
	if (fulfilled) {
		if (dueD && dueD < now)
			return {
				key: "completed",
				label: isMobile ? "Completed" : "Upload Completed",
				icon: (s) => <IconFileMusic size={s} />,
			};
		return {
			key: "submitAgain",
			label: isMobile
				? "Completed"
				: "Upload Completed - Submit Again?",
			icon: (s) => <IconFileMusic size={s} />,
		};
	}
	if (dueD && dueD < now)
		return {
			key: "pastDue",
			label: "Past Due",
			icon: (s) => <IconExclamationMark size={s} />,
		};
	return {
		key: "notSubmitted",
		label: isMobile ? "Upload" : "Submit Your Video",
		icon: (s) => <IconFileUpload size={s} />,
	};
};

const injectPulseAnimation = () => {
	if (document.getElementById("pulse-keyframes")) return;
	const style = document.createElement("style");
	style.id = "pulse-keyframes";
	style.innerHTML = `
		@keyframes pulseButton {
		0%, 100% { transform: scale(1); }
		50% { transform: scale(1.08); }
		}
	`;
	document.head.appendChild(style);
};

/* ----------------------------- shared buttons ---------------------------- */

const BackingTrackButton: React.FC<{
	submission: ProjectSubmission;
	fontSz: number;
	isMobile?: boolean;
}> = ({ submission, fontSz, isMobile }) => {
	const theme = useMantineTheme();
	const onClick = useCallback(() => {
		const url = `${appSettings.S3BucketBaseUrl}InstrumentalistTaskFiles/${submission.id}.mp3`;
		const file = `${submission.submissionProjectName}-BackingTrack.mp3`.replace(
			/\s+/g,
			"_"
		);
		Utils.downloadBlob(url, file, <IconDownload />, <IconAlertCircle />);
	}, [submission, fontSz]);

	return (
		<Button
			variant="default"
			size={isMobile ? "compact-xs" : "compact-sm"}
			c={theme.colors.blue[8]}
			onClick={(e) => {
				e.stopPropagation();
				onClick();
			}}
			rightSection={<IconDownload size={fontSz} />}
		>
			{isMobile ? (
				<IconFileMusic size={12} />
			) : (
				<Text fz={fontSz}>Backing Track</Text>
			)}
		</Button>
	);
};

const ExampleFeedbackButton: React.FC<{
	fontSz: number;
	isMobile?: boolean;
}> = ({ fontSz, isMobile }) => {
	const theme = useMantineTheme();
	const onClick = useCallback(() => {
		const url = `${appSettings.S3BucketBaseUrl}Videos/ExampleFeedback.mp4`;
		const file = "ExampleFeedback.mp4";
		Utils.downloadBlob(url, file, <IconDownload />, <IconAlertCircle />);
	}, [fontSz]);

	return (
		<Button
			variant="default"
			size={isMobile ? "compact-xs" : "compact-sm"}
			c={theme.colors.blue[8]}
			onClick={(e) => {
				e.stopPropagation();
				onClick();
			}}
			rightSection={<IconDownload size={fontSz} />}
		>
			{isMobile ? (
				<IconUserCircle size={12} />
			) : (
				<Text fz={fontSz}>Example Feedback</Text>
			)}
		</Button>
	);
};

const SheetMusicButton: React.FC<{
	submission: ProjectSubmission;
	task: InstrumentalistTask;
	fontSz: number;
	isMobile?: boolean;
}> = ({ submission, task, fontSz, isMobile }) => {
	const theme = useMantineTheme();
	const onClick = useCallback(() => {
		const url = `${appSettings.S3BucketBaseUrl}InstrumentalistTaskFiles/${task.id}.pdf`;
		const file = `${submission.submissionProjectName}-${task.instrument.name}-SheetMusic.pdf`.replace(
			/\s+/g,
			"_"
		);
		Utils.downloadBlob(url, file, <IconDownload />, <IconAlertCircle />);
	}, [submission, task, fontSz]);

	return (
		<Button
			variant="default"
			size={isMobile ? "compact-xs" : "compact-sm"}
			c={theme.colors.blue[8]}
			onClick={onClick}
			rightSection={<IconDownload size={fontSz} />}
		>
			{isMobile ? (
				<IconFileDescription size={12} />
			) : (
				<Text fz={fontSz}>Sheet Music</Text>
			)}
		</Button>
	);
};

const UploadButton: React.FC<{
	labelState: UploadState;
	isUploading: boolean;
	tooltip?: string;
	onPickFile: (e: React.MouseEvent<HTMLButtonElement>) => void;
	fontSize: number;
	bgColor?: string;
	isMobile?: boolean;
}> = ({
	labelState,
	isUploading,
	tooltip,
	onPickFile,
	fontSize,
	bgColor,
	isMobile,
}) => {
		const Btn = (
			<Tooltip
				disabled={labelState.key !== "processing" && !isUploading}
				label={
					labelState.key === "processing"
						? "This video is being processed."
						: isUploading
							? "Uploading..."
							: undefined
				}
				position="top"
				withArrow
			>
				<Button
					fz={fontSize}
					variant="default"
					size={isMobile ? "compact-xs" : "compact-sm"}
					bg={bgColor}
					onClick={(e) => {
						if (isUploading) return;
						onPickFile(e);
					}}
					leftSection={<IconUpload size={fontSize} />}
					loading={labelState.key === "processing" || isUploading}
					loaderProps={{ type: "bars" }}
				>
					<Text fz={fontSize}>{labelState.key === "submitAgain" || labelState.key === "completed" ? "Submit Again?" : "Upload Video"}</Text>
				</Button>
			</Tooltip>
		);
		return labelState.key === "error" && tooltip ? (
			<Tooltip label={tooltip} position="top" withArrow>
				{Btn}
			</Tooltip>
		) : (
			Btn
		);
	};

/* --------------------------------- main comp -------------------------------- */

interface TaskRowProps {
	row: Row;
	isMobile: boolean;
	fzSmall: number;
	onRequestUploadTask: (taskId: string, includesFeedback: boolean) => void;
	onRequestClaimTask: (taskId: string, includesFeedback: boolean) => void;
	uploadingIds: string[];
}

const TaskRow: React.FC<TaskRowProps> = ({
	row,
	isMobile,
	fzSmall,
	onRequestUploadTask,
	onRequestClaimTask,
	uploadingIds,
}) => {
	const [showClaimTimerTooltip, setShowClaimTimerTooltip] = useState<boolean>(false);
	const [showFeedbackTooltip, setShowFeedbackTooltip] = useState<boolean>(false);

	const session = useContext(SessionContext);
	if (!session) return null;
	const theme = useMantineTheme();
	const task = row.task;
	const isClaimedOther = row.kind === "offer-claimed-by-other";
	const submission = row.submission;

	const userName =
		row.submission.projectOwner?.username ||
		"N/A User";

	const dueDate = toLocal(
		row.submission.instrumentalistDueDate,
		isMobile
	);
	const projectName =
		row.submission.submissionProjectName ||
		"N/A Project";

	const isPastDue = (() => {
		const dueD = asDate(submission.instrumentalistDueDate);
		if (!dueD) return false;
		return dueD < new Date();
	})();

	const offerIncludesFeedback = submission.feedbackInstrumentalistTaskId === row.task?.id;

	const rowBg = isClaimedOther
		? theme.colors.gray[3]
		: row.kind === "claimed-feedback"
			? submission.feedbackStatus === "Uploaded"
				? theme.colors.green[1]
				: isPastDue
					? theme.colors.red[1]
					: theme.colors.yellow[1]
			: task?.instrumentalist?.id === session.user?.id
				? task?.taskStatus === "Uploaded"
					? theme.colors.green[1]
					: isPastDue
						? theme.colors.red[1]
						: theme.colors.yellow[1]
				: theme.white;

	return (
		<Table.Tr
			key={(task?.id ?? "") + row.kind}
			bg={rowBg}
			opacity={
				row.kind == "offer-claimed-by-other" ||
					(row.kind == "claimed-performance" && task?.taskStatus === "Uploaded") ||
					(row.kind == "claimed-feedback" && submission.feedbackStatus === "Uploaded")
					? 0.5 : 1
			}
		>
			<Table.Td ta="center" fz={fzSmall}>
				{task?.taskStatus === "Uploaded" ||
					row.kind === "offer" ||
					row.kind === "offer-claimed-by-other" ? (
					<Text fz={fzSmall}>{dueDate}</Text>
				) : (
					<Stack align="center">
						{!isMobile && <Text fz={fzSmall}>{dueDate}</Text>}
						<Countdown
							targetDate={submission.instrumentalistDueDate}
							textSz={fzSmall}
						/>
					</Stack>
				)}
			</Table.Td>

			<Table.Td ta="center" fz={fzSmall}>
				{isMobile || !userName ? (
					<Text fz={fzSmall}>{projectName}</Text>
				) : (
					<Stack align="center">
						<Text fz={fzSmall} fw="700">
							{projectName}
						</Text>
						<Text fz={fzSmall}> by {userName}</Text>
					</Stack>
				)}
			</Table.Td>

			{/* Instrument / Type */}
			<Table.Td ta="center" fz={fzSmall}>
				{row.kind === "claimed-feedback" ? (
					<Tooltip
						label={
							<Stack gap="xs">
								<Text fz={fzSmall}>
									Please record a short (one-minute) video of positive and
								</Text>
								<Text fz={fzSmall}>
									constructive feedback about this arrangement for the creator.
								</Text>
							</Stack>
						}
						position="bottom"
						withArrow
						opened={showFeedbackTooltip}
					>
						<Group
							onClick={() =>
								setShowFeedbackTooltip(!showFeedbackTooltip)
							}
							gap="sm"
							justify="center"
						>
							<Text fz={fzSmall}>Feedback Video</Text>
							<ActionIcon
								c="gray"
								variant="outline"
								size="xs"
							>
								<IconQuestionMark />
							</ActionIcon>
						</Group>
					</Tooltip>
				) : task ? (
					row.kind === "offer" ? (
						<Stack align="center">
							<Text fz={fzSmall}>{task.instrument.name}</Text>

							{offerIncludesFeedback && (
								<Tooltip
									label="This task also asks for a positive and constructive feedback video about the project."
									position="top"
									withArrow
								>
									<Badge color="blue" variant="filled" size="md">
										+ Feedback Video
									</Badge>
								</Tooltip>
							)}
						</Stack>
					) : row.kind === "offer-claimed-by-other" ? (
						<Text fz={fzSmall} c="dimmed">
							—
						</Text>
					) : (
						<Text fz={fzSmall}>{task.instrument.name}</Text>
					)
				) : (
					"—"
				)}
			</Table.Td>

			<Table.Td ta="center" fz={fzSmall}>
				<Group justify="center">
					<BackingTrackButton
						submission={submission}
						fontSz={fzSmall}
						isMobile={isMobile}
					/>
					{task ? (
						<SheetMusicButton
							submission={submission}
							task={task}
							fontSz={fzSmall}
							isMobile={isMobile}
						/>
					) : (
						<ExampleFeedbackButton
							fontSz={fzSmall}
							isMobile={isMobile}
						/>
					)}
				</Group>
			</Table.Td>

			{/* Action */}
			<Table.Td ta="center" fz={fzSmall}>
				{row.kind === "claimed-performance" && task ? (
					<UploadButton
						labelState={computeUploadState(
							task.taskStatus,
							task.dateFulfilled,
							submission.instrumentalistDueDate,
							isMobile
						)}
						isUploading={uploadingIds.includes(task.id)}
						onPickFile={(e) => {
							e.stopPropagation();
							onRequestUploadTask(task.id, false);
						}}
						fontSize={fzSmall}
						isMobile={isMobile}
					/>
				) : row.kind === "claimed-feedback" ? (
					<UploadButton
						labelState={computeUploadState(
							submission.feedbackStatus,
							submission.feedbackDateFulfilled,
							submission.instrumentalistDueDate,
							isMobile
						)}
						isUploading={uploadingIds.includes(submission.id)}
						onPickFile={(e) => {
							e.stopPropagation();
							onRequestUploadTask(submission.id, true);
						}}
						fontSize={fzSmall}
						isMobile={isMobile}
					/>
				) : row.kind === "offer" && task ? (
					<Stack align="center">
						<Button
							variant="filled"
							size={isMobile ? "compact-xs" : "compact-sm"}
							color="green"
							leftSection={!isMobile && <IconCheck size={fzSmall} />}
							onClick={(e) => {
								e.stopPropagation();
								onRequestClaimTask(task.id, offerIncludesFeedback);
							}}
							style={{
								animation: "pulseButton 1.8s ease-in-out infinite",
								transformOrigin: "center",
							}}
						>
							<Text fz={fzSmall}>Claim $22.50 Task!</Text>
						</Button>

						<Tooltip
							label={
								<Stack gap="xs">
									<Text fz={fzSmall}>
										After this timer runs out, this gig will be
										offered to another
									</Text>
									<Text fz={fzSmall}>
										cohort of musicians. Claim now to secure this gig!
									</Text>
								</Stack>
							}
							position="bottom"
							withArrow
							opened={showClaimTimerTooltip}
						>
							<Group
								onClick={() =>
									setShowClaimTimerTooltip(!showClaimTimerTooltip)
								}
								gap="sm"
							>
								<Box w={45}>
									{/* This helps to keep the width of the countdown timer the same*/}
									<Countdown
										targetDate={task.currentOfferDeadline}
										activeTimer
										textSz={12}
										pastDueText="0:00"
									/>
								</Box>
								<ActionIcon
									c="gray"
									variant="outline"
									size="xs"
								>
									<IconQuestionMark />
								</ActionIcon>
							</Group>
						</Tooltip>
					</Stack>
				) : row.kind === "offer-claimed-by-other" ? (
					<Stack align="center">
						<Tooltip
							label="Another musician has already claimed this gig. Be quick to claim other gigs!"
							position="bottom"
							withArrow
						>
							<Box>
								<Text fz={fzSmall} c="red">
									Claimed by another musician.
								</Text>
							</Box>
						</Tooltip>
					</Stack>
				) : (
					<Text fz={fzSmall} c="dimmed">
						—
					</Text>
				)}
			</Table.Td>
		</Table.Tr>
	);
};

/* --------------------------- row building -------------------------- */

type RowKind = "claimed-performance" | "claimed-feedback" | "offer" | "offer-claimed-by-other";

type Row = {
	kind: RowKind;
	submission: ProjectSubmission; // For feedback tasks
	task?: InstrumentalistTask; // For performance tasks
};

/* ------------------------------- main component ----------------------------- */

const InstrumentalistTasks: React.FC = () => {
	const [projectSubmissions, setProjectSubmissions] = useState<ProjectSubmission[] | undefined>(
		undefined
	);

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [uploadingIds, setUploadingIds] = useState<string[]>([]);

	// Modal state for Responsibility Agreement
	const [claimAgreementOpen, setClaimAgreementOpen] = useState<boolean>(false);
	const [uploadAgreementOpen, setUploadAgreementOpen] = useState<boolean>(false);
	const [pendingTaskId, setPendingTaskId] = useState<string | null>(null);
	const [pendingTaskIncludesFeedback, setPendingTaskIncludesFeedback] = useState<boolean>(false);

	const theme = useMantineTheme();
	const isMobile = !!useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
	const fzSmall = isMobile ? 10 : 14;

	useEffect(() => {
		injectPulseAnimation();
	}, []);

	const session = useContext(SessionContext);
	if (!session) return null;

	const skeletonRows = useMemo(() => {
		const cell = <Skeleton height={74} ta="center" />;
		const row = (
			<Table.Tr>
				<Table.Td ta="center">{cell}</Table.Td>
				<Table.Td ta="center">{cell}</Table.Td>
				<Table.Td ta="center">{cell}</Table.Td>
				<Table.Td ta="center">{cell}</Table.Td>
				<Table.Td ta="center">{cell}</Table.Td>
			</Table.Tr>
		);
		return Array.from({ length: 4 }, (_, i) =>
			React.cloneElement(row, { key: `sk-${i}` })
		);
	}, []);

	const loadProjectSubmissions = useCallback(async () => {
		if (!session.user || !session.token) return;
		setIsLoading(true);
		setProjectSubmissions(undefined);
		try {
			const res = await session.getInstrumentalistProjectSubmissions(session.user.id);
			setProjectSubmissions(res.items);
		} finally {
			setIsLoading(false);
		}
	}, [session]);

	useEffect(() => {
		void loadProjectSubmissions();
	}, [loadProjectSubmissions]);

	

	/* --------------------------- upload handlers --------------------------- */

	const pickTaskFile = useCallback(
		(taskId: string) => {
			if (uploadingIds.includes(taskId)) return;
			const input = document.createElement("input");
			input.type = "file";
			input.accept = ".mp4,.mov,.avi,.mkv,.webm";
			input.onchange = (e) => {
				const file = (e.target as HTMLInputElement).files?.[0];
				if (file) void uploadTask(file, taskId);
			};
			input.click();
		},
		[uploadingIds]
	);

	const pickFeedbackFile = useCallback(
		(submissionId: string) => {
			if (uploadingIds.includes(submissionId)) return;
			const input = document.createElement("input");
			input.type = "file";
			input.accept = ".mp4,.mov,.avi,.mkv,.webm";
			input.onchange = (e) => {
				const file = (e.target as HTMLInputElement).files?.[0];
				if (file) void uploadFeedback(file, submissionId);
			};
			input.click();
		},
		[uploadingIds]
	);

	const uploadTask = useCallback(
		async (file: File, taskId: string) => {
			const token = session.token;
			if (!token) return;

			setUploadingIds((prev) => [...prev, taskId]);
			const shouldContinue = await Utils.confirmLargeUpload(
				file,
				500 * 1024 * 1024
			);
			if (!shouldContinue) {
				setUploadingIds((prev) => prev.filter((x) => x !== taskId));
				return;
			}

			const totalBytes = file.size;
			const controller = new AbortController();

			const beforeUnload = (e: BeforeUnloadEvent) => {
				e.preventDefault();
				e.returnValue = "";
			};

			const renderProgress = (percent: number, onCancel: () => void) => (
				<div style={{ width: "100%" }}>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							marginBottom: 6,
						}}
					>
						<span>{percent.toFixed(0)}%</span>
						<button
							type="button"
							onClick={onCancel}
							style={{
								border: "none",
								background: "transparent",
								color: "#fa5252",
								cursor: "pointer",
								fontSize: 12,
							}}
							aria-label="Cancel upload"
						>
							Cancel upload
						</button>
					</div>
					<div
						style={{
							height: 8,
							background: "rgba(0,0,0,0.1)",
							borderRadius: 4,
							overflow: "hidden",
						}}
					>
						<div
							style={{
								height: "100%",
								width: `${Math.max(0, Math.min(100, percent))}%`,
								background: "#228be6",
								transition: "width 150ms linear",
							}}
						/>
					</div>
				</div>
			);

			const notificationId = Utils.displayNotification(
				"Uploading video... Please do not close this page or navigate away.",
				"Starting upload...",
				"blue",
				"loading",
				false,
				false
			);

			window.addEventListener("beforeunload", beforeUnload);

			let lastUpdate = 0;
			const onProgress = (uploadedBytes: number) => {
				const pct = totalBytes ? (uploadedBytes / totalBytes) * 100 : 0;
				const now = performance.now();
				if (now - lastUpdate < 300 && pct < 100) return;
				lastUpdate = now;

				if (pct < 100) {
					Utils.updateNotification(
						notificationId,
						"Uploading video... Please do not close this page or navigate away.",
						renderProgress(pct, () => controller.abort()),
						"blue",
						"loading",
						false,
						false
					);
				} else {
					Utils.updateNotification(
						notificationId,
						"Upload complete! We are now processing the video.",
						"You may navigate away from this page and return later.",
						"green",
						<IconFileUpload size={16} />,
						true,
						false
					);

					setTimeout(() => {
						setIsLoading(true);
						session.clearProjectSubmissionsCache();
						loadProjectSubmissions();
					}, 5000);
				}
			};

			try {
				await ConductionAPI.submitInstrumentalistTask(
					taskId,
					file,
					token,
					{
						onProgress,
						signal: controller.signal,
					}
				);

				await session.clearProjectSubmissionsCache();
				await loadProjectSubmissions();
			} catch (error) {
				const isAbort =
					error instanceof DOMException && error.name === "AbortError";
				const msg =
					error instanceof Error
						? error.message
						: "Unknown error";

				if (isAbort) {
					Utils.updateNotification(
						notificationId,
						"Upload canceled",
						undefined,
						"yellow",
						<IconAlertCircle size={16} />,
						true,
						8000
					);
				} else {
					Utils.updateNotification(
						notificationId,
						"Upload Failed",
						msg ||
						"Try reducing file size, then try again.",
						"red",
						<IconAlertCircle size={16} />,
						true,
						false
					);
				}
			} finally {
				setUploadingIds((prev) => prev.filter((x) => x !== taskId));
				window.removeEventListener("beforeunload", beforeUnload);
			}
		},
		[session, fzSmall, loadProjectSubmissions]
	);

	const uploadFeedback = useCallback(
		async (file: File, submissionId: string) => {
			const token = session.token;
			if (!token) return;

			setUploadingIds((prev) => [...prev, submissionId]);
			const shouldContinue = await Utils.confirmLargeUpload(
				file,
				500 * 1024 * 1024
			);
			if (!shouldContinue) {
				setUploadingIds((prev) =>
					prev.filter((x) => x !== submissionId)
				);
				return;
			}

			const controller = new AbortController();
			const notificationId = Utils.displayNotification(
				"Uploading video... Please do not close this page or navigate away.",
				"Starting upload...",
				"blue",
				"loading",
				false,
				false
			);

			window.addEventListener(
				"beforeunload",
				(e: BeforeUnloadEvent) => {
					e.preventDefault();
					e.returnValue = "";
				}
			);

			try {
				await ConductionAPI.submitInstrumentalistFeedback(
					submissionId,
					file,
					token,
					{
						signal: controller.signal,
					}
				);

				Utils.updateNotification(
					notificationId,
					"Upload complete!",
					"We are processing your feedback video.",
					"green",
					<IconFileUpload size={16} />,
					true,
					false
				);

				setTimeout(() => {
					setIsLoading(true);
					session.clearProjectSubmissionsCache();
					loadProjectSubmissions();
				}, 5000);
			} catch (error) {
				const msg =
					error instanceof Error
						? error.message
						: "Unknown error";
				Utils.updateNotification(
					notificationId,
					"Upload Failed",
					msg,
					"red",
					<IconAlertCircle size={16} />,
					true,
					false
				);
			} finally {
				setUploadingIds((prev) =>
					prev.filter((x) => x !== submissionId)
				);
			}
		},
		[session, fzSmall, loadProjectSubmissions]
	);

	const claimOfferedTask = useCallback(
		async (taskId: string) => {
			if (!session.token) return;

			const notifId = Utils.displayNotification(
				"Claiming gig...",
				"One moment...",
				"blue",
				"loading",
				false,
				false
			);
			try {
				const res = await ConductionAPI.claimInstrumentalistTask(
					session.token,
					taskId
				);
				if (res.alreadyClaimed) {
					Utils.updateNotification(
						notifId,
						"Already claimed",
						res.message ||
						"Sorry, another instrumentalist claimed this gig just before you.",
						"yellow",
						<IconAlertCircle size={16} />,
						true,
						8000
					);
					void session.clearProjectSubmissionsCache();
					void loadProjectSubmissions();
				} else {
					Utils.updateNotification(
						notifId,
						"Gig claimed!",
						"You can now upload your video in this project.",
						"green",
						<IconCheck size={16} />,
						true,
						6000
					);
					void session.clearProjectSubmissionsCache();
					void loadProjectSubmissions();
				}
			} catch (err: any) {
				Utils.updateNotification(
					notifId,
					"Error claiming gig",
					err?.message || "Please try again.",
					"red",
					<IconAlertCircle size={16} />,
					true,
					8000
				);
			}
		},
		[session, loadProjectSubmissions]
	);

	const buildUploadStatements = (feedbackStatement: boolean): AgreementStatement[] => {
		const statements: AgreementStatement[] = feedbackStatement
			? [
				{
					key: "quality",
					label: (
						<Text fz="sm" fw={700}>
							I have confirmed that my feedback recording has clear audio and video.
						</Text>
					),
				},
				{
					key: "appropriate",
					label: (
						<Text fz="sm" fw={700}>
							My feedback video is respectful, constructive, and appropriate for a school setting.
						</Text>
					),
				},
			]
			: [
				{
					key: "quality",
					label: (
						<Text fz="sm">
							I have confirmed that my recording has clear audio and video.
						</Text>
					),
				},
				{
					key: "backing-track",
					label: (
						<Text fz="sm">
							I followed the <strong>backing track closely</strong> during my recording.
						</Text>
					),
				},
				{
					key: "headphones",
					label: (
						<Text fz="sm">
							The backing track is <strong>not audible</strong> in my recording.
						</Text>
					),
				},
			];

		return statements;
	};

	const buildClaimStatements = (includeFeedbackStatement: boolean): AgreementStatement[] => {
		const base: AgreementStatement[] = [
			{
				key: "deadline",
				label: (
					<Text fz="sm">
						I understand that my recordings must be uploaded{" "}
						<strong>on or before the posted due date.</strong>
					</Text>
				),
			},
			{
				key: "quality",
				label: (
					<Text fz="sm">
						I will record in <strong>landscape orientation</strong> using a
						clear, reasonably high-quality camera and microphone.
					</Text>
				),
			},
			{
				key: "appropriate",
				label: (
					<Text fz="sm">
						I will wear <strong>school appropriate attire</strong> in the video and avoid having any inappropriate content in the background.
					</Text>
				),
			},
			{
				key: "backing-track",
				label: (
					<Text fz="sm">
						I will follow the <strong>backing track closely</strong> so that my
						performance can be easily synchronized with other musicians.
					</Text>
				),
			},
			{
				key: "headphones",
				label: (
					<Text fz="sm">
						I will use headphones or otherwise keep the backing track{" "}
						<strong>out of the final audio</strong> so it cannot be heard in my
						recording.
					</Text>
				),
			},
		];

		const feedback: AgreementStatement[] = includeFeedbackStatement
			? [
				{
					key: "feedback-video",
					label: (
						<Text fz="sm" fw={700}>
							I am aware that this task also asks me to record a short (one-minute)
							video of positive and constructive feedback about this arrangement
							for the creator.
						</Text>
					),
				},
			]
			: [];

		return [...base, ...feedback];
	};

	const handleRequestClaimTask = useCallback((taskId: string, includesFeedback: boolean) => {
		setPendingTaskId(taskId);
		setPendingTaskIncludesFeedback(includesFeedback);
		setClaimAgreementOpen(true);
	}, []);

	const handleRequestUploadTask = useCallback((taskId: string, includesFeedback: boolean) => {
		setPendingTaskId(taskId);
		setPendingTaskIncludesFeedback(includesFeedback);
		setUploadAgreementOpen(true);
	}, []);

	const handleConfirmClaimAgreement = useCallback(() => {
		if (!pendingTaskId) return;
		void claimOfferedTask(pendingTaskId);
		setClaimAgreementOpen(false);
		setPendingTaskIncludesFeedback(false);
		setPendingTaskId(null);
	}, [pendingTaskId, claimOfferedTask]);

	const handleConfirmUploadAgreement = useCallback(() => {
		if (!pendingTaskId) return;
		if (pendingTaskIncludesFeedback) {
			void pickFeedbackFile(pendingTaskId);
		} else {
			void pickTaskFile(pendingTaskId);
		}

		setUploadAgreementOpen(false);
		setPendingTaskIncludesFeedback(false);
		setPendingTaskId(null);
	}, [pendingTaskId, pendingTaskIncludesFeedback, pickFeedbackFile, pickTaskFile]);

	const handleCloseClaimAgreement = useCallback(() => {
		setClaimAgreementOpen(false);
		setPendingTaskIncludesFeedback(false);
		setPendingTaskId(null);
	}, []);

	const handleCloseUploadAgreement = useCallback(() => {
		setUploadAgreementOpen(false);
		setPendingTaskIncludesFeedback(false);
		setPendingTaskId(null);
	}, []);

	/* ------------------------ build combined submissions ------------------------ */

	const rows = useMemo(() => {
		const unsortedRows: Row[] = [];
		const currentUserId = session.user?.id;

		if (projectSubmissions) {
			for (const s of projectSubmissions) {
				for (const t of s.instrumentalistTasks) {
					const assignedInstrumentalist = t.instrumentalist;
					if (assignedInstrumentalist != null && assignedInstrumentalist.id === currentUserId) {
						// This task is claimed by the current user
						unsortedRows.push({
							kind: "claimed-performance",
							submission: s,
							task: t
						});
						// Also check if there's a feedback task assigned to me
						if (s.feedbackInstrumentalistTaskId === t.id) {
							unsortedRows.push({
								kind: "claimed-feedback",
								submission: s
							});
						}
					} else if (t.instrumentalistsToOffer.some((iu) => iu.id === currentUserId)) {
						// 
						if (assignedInstrumentalist == null) {
							// No editor assigned - this is an open offer
							unsortedRows.push({
								kind: "offer",
								submission: s,
								task: t
							});
						} else {
							// This offer has been claimed by another editor
							unsortedRows.push({
								kind: "offer-claimed-by-other",
								submission: s,
								task: t
							});
						}
					}
				}
			}
		}

		const kindRank = (kind: RowKind): number => {
			switch (kind) {
				case "offer":             // unclaimed offers at the top
					return 0;
				case "claimed-performance":             // claimed by current user
					return 1;
				case "claimed-feedback":             // feedback claimed by me
					return 1;
				case "offer-claimed-by-other": // claimed by someone else, show but last
					return 2;
				default:
					return 3;
			}
		};

		const statusRank = (uploadStatus: UploadStatus): number => {
			switch (uploadStatus) {
				case "Error":
					return 0;
				case "NotUploaded":
					return 1;
				case "Uploading":
					return 2;
				case "Uploaded":
					return 3;
				default:
					return 4;
			}
		};

		const sortedRows = [...unsortedRows].sort((a, b) => {
			// 1) Primary sort: by kind
			const kindDiff = kindRank(a.kind) - kindRank(b.kind);
			if (kindDiff !== 0) return kindDiff;

			const aStatus = a.kind === "claimed-performance" || a.kind === "offer"
				? a.task!.taskStatus
				: a.submission.feedbackStatus;
			const bStatus = b.kind === "claimed-performance" || b.kind === "offer"
				? b.task!.taskStatus
				: b.submission.feedbackStatus;

			const statusDiff = statusRank(aStatus) - statusRank(bStatus);
			if (statusDiff !== 0) return statusDiff;

			// 2) Secondary sort: by relevant date
			const aSortDate = a.kind !== "offer" && a.kind !== "offer-claimed-by-other"
				? a.submission.instrumentalistDueDate
				: a.submission.currentOfferDeadline;

			const bSortDate = b.kind !== "offer" && b.kind !== "offer-claimed-by-other"
				? b.submission.instrumentalistDueDate
				: b.submission.currentOfferDeadline;

			const ta = aSortDate ? +aSortDate : Number.MAX_SAFE_INTEGER;
			const tb = bSortDate ? +bSortDate : Number.MAX_SAFE_INTEGER;

			return ta - tb;
		});

		return sortedRows;
	}, [projectSubmissions, session.user?.id]);

	const allLoading = isLoading;
	const unfinishedTasksCount = rows
		.filter(
			(r) =>
			((r.kind === "claimed-performance" && r.task?.taskStatus == "NotUploaded") ||
				(r.kind === "claimed-feedback" && r.submission.feedbackStatus == "NotUploaded"))
		).length;
	const openOffersCount = rows.filter((r) => r.kind === "offer").length;

	return (
		<Box
			p="xs"
			w="100%"
			h="100%"
			style={{
				display: "grid",
				gridTemplateRows: "auto 1fr auto",
				overflow: "hidden",
				minHeight: 0,
			}}
		>
			{/* Header */}
			<Group w="100%" justify="space-between" align="center" p="xs">
				<Text c={theme.colors.blue[0]} fz={fzSmall}>
					Claim gigs, download the sheet music and backing tracks, and upload
					your performance videos here.
				</Text>

				<Group
					w="100%"
					maw="500px"
					justify="space-between"
					align="center"
					p="xs"
				>
					{allLoading ? (
						<Group>
							<Loader size={fzSmall * 2} color={theme.colors.blue[0]} />
							<Card
								withBorder
								p="xs"
								bg={theme.colors.blue[1]}
								w={fzSmall * 18}
								h={fzSmall * 5}
								padding="xs"
							/>
						</Group>
					) : (
						<Group>
							{unfinishedTasksCount > 0 || openOffersCount > 0 ? (
								<IconAlertCircle
									size={fzSmall * 2}
									color={theme.colors.yellow[6]}
								/>
							) : (
								<IconCheck
									size={fzSmall * 2}
									color={theme.colors.green[6]}
								/>
							)}
							<Card
								withBorder
								p="xs"
								bg={theme.colors.blue[1]}
								w={fzSmall * 18}
								h={fzSmall * 5}
								padding="xs"
							>
								<Stack gap={2}>
									{unfinishedTasksCount > 0 ? (
										<Text
											c={theme.colors.blue[12]}
											fz={fzSmall}
										>
											You have{" "}
											<strong>
												{unfinishedTasksCount} unfinished
												task
												{unfinishedTasksCount === 1 ? "" : "s"}
											</strong>
											.
										</Text>
									) : (
										<Text
											c={theme.colors.blue[12]}
											fz={fzSmall}
										>
											You have no unfinished tasks.
										</Text>
									)}

									<Text
										c={theme.colors.blue[12]}
										fz={fzSmall}
									>
										You have{" "}
										<strong>
											{openOffersCount} unclaimed offer
											{openOffersCount === 1 ? "" : "s"}
										</strong>
										.
									</Text>
								</Stack>
							</Card>
						</Group>
					)}

					<Button
						leftSection={<IconRefresh size={fzSmall} />}
						onClick={() => {
							setIsLoading(true);
							void session.clearProjectSubmissionsCache();
							void loadProjectSubmissions();
						}}
						c={theme.colors.blue[0]}
						disabled={allLoading}
					>
						<Text fz={fzSmall}>Refresh</Text>
					</Button>
				</Group>
			</Group>

			<ScrollArea
				h="100%"
				w="100%"
				bg={theme.colors.blue[0]}
				type="auto"
				viewportProps={{ style: { height: "100%" } }}
				styles={{ viewport: { overflowX: "hidden" } }}
			>
				<Table w="100%" h="100%" striped highlightOnHover>
					{/* Table Header */}
					<Table.Thead>
						<Table.Tr>
							<Table.Th fz={fzSmall} ta="center">
								<Text fz={fzSmall}>Deadline</Text>
							</Table.Th>
							<Table.Th fz={fzSmall} ta="center">
								<Text fz={fzSmall}>Project</Text>
							</Table.Th>
							<Table.Th fz={fzSmall} ta="center">
								<Text fz={fzSmall}>Your Role</Text>
							</Table.Th>
							<Table.Th fz={fzSmall} ta="center">
								<Text fz={fzSmall}>Files</Text>
							</Table.Th>
							<Table.Th fz={fzSmall} ta="center">
								<Text fz={fzSmall}>Action</Text>
							</Table.Th>
						</Table.Tr>
					</Table.Thead>

					{allLoading ? (
						skeletonRows
					) : rows.length === 0 ? (
						<Table.Tr>
							<Table.Td colSpan={5}>
								<Text ta="center">No tasks or offers right now</Text>
							</Table.Td>
						</Table.Tr>
					) : (
						rows.map(
							(row) => {
								return (
									<TaskRow
										key={
											row.submission.id +
											"-" +
											row.kind +
											(row.task ? "-" + row.task.id : "")
										}
										row={row}
										isMobile={isMobile}
										fzSmall={fzSmall}
										onRequestUploadTask={handleRequestUploadTask}
										onRequestClaimTask={handleRequestClaimTask}
										uploadingIds={uploadingIds}
									/>
								)
							}
						)
					)}
				</Table>
			</ScrollArea>

			{/* Responsibility Agreement Modal for claiming tasks */}
			<ResponsibilityAgreementModal
				titleText="Responsibility Agreement"
				promptText="Before you claim this gig, please read each statement and check all boxes to confirm your agreement."
				buttonText="Claim Gig"
				opened={claimAgreementOpen && !!pendingTaskId}
				onClose={handleCloseClaimAgreement}
				onConfirm={handleConfirmClaimAgreement}
				statements={buildClaimStatements(pendingTaskIncludesFeedback)}
			/>

			{/* Responsibility Agreement Modal for uploading videos for a tasks */}
			<ResponsibilityAgreementModal
				titleText="Final Review Checklist"
				promptText="Before uploading your video, please read each statement and check each box to confirm your review."
				buttonText="Proceed to Upload"
				opened={uploadAgreementOpen && !!pendingTaskId}
				onClose={handleCloseUploadAgreement}
				onConfirm={handleConfirmUploadAgreement}
				statements={buildUploadStatements(pendingTaskIncludesFeedback)}
			/>
		</Box>
	);
};

export default InstrumentalistTasks;
